/**
 * League table, stale-while-revalidate: the last rows (memory, then localStorage) render
 * at once and a background request replaces them. One request is shared by every caller.
 */
import { fetchLeague, type LeagueRow } from './api.ts';
import { readJson, writeJson, writeString } from './storage.ts';

export const LEAGUE_CACHE_KEY = 'soile.league.v2';
/** A table younger than this is shown without asking the server again. */
const FRESH_MS = 60_000;

interface Cached {
  rows: LeagueRow[];
  at: number;
}

let memory: Cached | null = null;
let inflight: Promise<LeagueRow[]> | null = null;
const listeners = new Set<(rows: LeagueRow[]) => void>();

export function cachedLeague(): LeagueRow[] | null {
  memory ??= readJson<Cached>(LEAGUE_CACHE_KEY);
  return memory?.rows ?? null;
}

/** Fetches unless the cached table is fresh; resolves with the newest rows. */
export function refreshLeague(force = false): Promise<LeagueRow[]> {
  const cached = cachedLeague();
  if (!force && cached && memory && Date.now() - memory.at < FRESH_MS) return Promise.resolve(cached);
  inflight ??= fetchLeague()
    .then((rows) => {
      memory = { rows, at: Date.now() };
      writeJson(LEAGUE_CACHE_KEY, memory);
      listeners.forEach((listener) => listener(rows));
      return rows;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Warms the cache after start-up; failures are silent (the screen retries). */
export function prefetchLeague(): void {
  refreshLeague().catch(() => {});
}

export function subscribeLeague(listener: (rows: LeagueRow[]) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Forgets the table (sign-out: it marks the previous person's row as theirs). */
export function clearLeague(): void {
  memory = null;
  writeString(LEAGUE_CACHE_KEY, null);
}
